import React, { useState, useEffect } from 'react';
import { mockCollections } from '../data/mockCollections.js';
import './Header.css';

const translations = {
  de: {
    'navigation.home': 'HOME',
    'navigation.menu': 'MENU',
    'navigation.takeaway': 'TAKE AWAY',
    'navigation.banquets': 'BANQUETS',
    'navigation.about': 'ÜBER UNS',
    'navigation.contact': 'KONTAKT',
    'header.orderCall': 'Bestellung anrufen:',
    'header.cart': 'Warenkorb',
    'header.items': 'Artikel',
    'header.language': 'Sprache',
    'header.currency': 'Währung',
  },
  en: {
    'navigation.home': 'HOME',
    'navigation.menu': 'MENU',
    'navigation.takeaway': 'TAKE AWAY',
    'navigation.banquets': 'BANQUETS',
    'navigation.about': 'ABOUT US',
    'navigation.contact': 'CONTACT US',
    'header.orderCall': 'Order call:',
    'header.cart': 'Cart',
    'header.items': 'items',
    'header.language': 'Language',
    'header.currency': 'Currency',
  },
};

const getTranslation = (lang, key) => {
  return translations[lang]?.[key] || translations['de']?.[key] || key;
};

const saveLanguage = lang => {
  localStorage.setItem('selectedLanguage', lang);
};

export default function Header({ logoUrl, settings = {} }) {
  const [cartCount, setCartCount] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('de');
  const [selectedCurrency, setSelectedCurrency] = useState('CHF');
  const [collections, setCollections] = useState([]);
  const [showCatalogMenu, setShowCatalogMenu] = useState(false);
  const [showMobileDropdown, setShowMobileDropdown] = useState(false);
  const [isLocalizationOpen, setIsLocalizationOpen] = useState(false);

  const getSavedLanguage = () => {
    return localStorage.getItem('selectedLanguage') || 'de';
  };

  useEffect(() => {
    const savedLang = getSavedLanguage();
    setSelectedLanguage(savedLang);
  }, []);

  useEffect(() => {
    if (!isLocalizationOpen) return;

    // Позиціонуємо меню відносно кнопки
    const updateMenuPosition = () => {
      const trigger = document.querySelector('.localization__trigger');
      const menu = document.querySelector('.localization__menu');

      if (trigger && menu) {
        const rect = trigger.getBoundingClientRect();
        menu.style.top = `${rect.bottom + 5}px`;
        menu.style.right = `${window.innerWidth - rect.right}px`;
      }
    };

    updateMenuPosition();

    // Перепозиціонуємо при скролі або зміні розміру вікна
    window.addEventListener('scroll', updateMenuPosition);
    window.addEventListener('resize', updateMenuPosition);

    const handleClickOutside = event => {
      const localizationElement = document.querySelector(
        '.header__localization'
      );
      if (localizationElement && !localizationElement.contains(event.target)) {
        setIsLocalizationOpen(false);
      }
    };

    // Додаємо невеликий таймаут щоб не закрити меню відразу після відкриття
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', updateMenuPosition);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [isLocalizationOpen]);

  useEffect(() => {
    const header = document.querySelector('.react-header');
    if (header) {
      if (settings.header_bg_color) {
        header.style.setProperty('--header-bg-color', settings.header_bg_color);
      }
      if (settings.header_text_color) {
        header.style.setProperty(
          '--header-text-color',
          settings.header_text_color
        );
      }
      if (settings.topbar_bg_color) {
        header.style.setProperty(
          '--header-topbar-bg',
          settings.topbar_bg_color
        );
      }
      if (settings.topbar_text_color) {
        header.style.setProperty(
          '--header-topbar-text',
          settings.topbar_text_color
        );
      }
      if (settings.link_hover_color) {
        header.style.setProperty(
          '--header-link-hover',
          settings.link_hover_color
        );
      }
      if (settings.logo_height) {
        header.style.setProperty('--logo-height', `${settings.logo_height}px`);
      }
      if (settings.header_bg_image) {
        header.style.setProperty(
          '--header-bg-image',
          `url(${settings.header_bg_image})`
        );
      }
      if (settings.header_bg_image_opacity) {
        header.style.setProperty(
          '--header-bg-opacity',
          settings.header_bg_image_opacity
        );
      }
    }
  }, [settings]);

  useEffect(() => {
    const updateCartCount = () => {
      fetch('/cart.js')
        .then(response => response.json())
        .then(data => setCartCount(data.item_count || 0))
        .catch(console.error);
    };

    updateCartCount();

    const handleHeaderCartUpdate = event => {
      if (event && event.detail && typeof event.detail.itemCount === 'number') {
        setCartCount(event.detail.itemCount);
      }
    };

    document.addEventListener('header:cart-updated', handleHeaderCartUpdate);

    return () => {
      document.removeEventListener(
        'header:cart-updated',
        handleHeaderCartUpdate
      );
    };
  }, []);

  useEffect(() => {
    fetch('/collections.json')
      .then(response => {
        if (!response.ok) {
          throw new Error('Collections API not available');
        }
        return response.json();
      })
      .then(data => {
        if (data.collections && data.collections.length > 0) {
          const firstFourCollections = data.collections.slice(0, 4);
          setCollections(firstFourCollections);
        } else {
          setCollections(mockCollections);
        }
      })
      .catch(error => {
        console.log('Using mock collections for development:', error.message);
        setCollections(mockCollections);
      });
  }, []);

  const languages = [
    { code: 'de', name: 'Deutsch' },
    { code: 'en', name: 'English' },
  ];

  const currencies = [{ code: 'CHF', symbol: 'Fr.' }];

  const getActiveMenuItem = () => {
    const currentTemplate = settings.current_template || '';
    const currentPageHandle = settings.current_page_handle || '';
    const currentCollectionHandle = settings.current_collection_handle || '';

    if (currentTemplate === 'index') {
      return 'HOME';
    }

    if (
      currentPageHandle === 'menu' ||
      (currentTemplate === 'page' && currentPageHandle === 'menu')
    ) {
      return 'MENU';
    }

    if (
      currentPageHandle === 'takeaway' ||
      (currentTemplate === 'page' && currentPageHandle === 'takeaway')
    ) {
      return 'TAKE AWAY';
    }

    if (
      currentPageHandle === 'banquets' ||
      (currentTemplate === 'page' && currentPageHandle === 'banquets')
    ) {
      return 'BANQUETS';
    }

    if (
      currentPageHandle === 'about' ||
      (currentTemplate === 'page' && currentPageHandle === 'about')
    ) {
      return 'ABOUT US';
    }

    if (currentTemplate.includes('collection') || currentCollectionHandle) {
      return 'MENU';
    }

    if (
      currentPageHandle === 'contact' ||
      (currentTemplate === 'page' && currentPageHandle === 'contact')
    ) {
      return 'CONTACT US';
    }

    return '';
  };

  const activeMenuItem = getActiveMenuItem();

  const menuItems = [
    {
      name: getTranslation(selectedLanguage, 'navigation.home'),
      url: '/',
      active: activeMenuItem === 'HOME',
    },
    {
      name: getTranslation(selectedLanguage, 'navigation.menu'),
      url: '/pages/menu',
      active: activeMenuItem === 'MENU',
    },
    {
      name: getTranslation(selectedLanguage, 'navigation.takeaway'),
      url: '/pages/takeaway',
      active: activeMenuItem === 'TAKE AWAY',
    },
    {
      name: getTranslation(selectedLanguage, 'navigation.banquets'),
      url: '/pages/banquets',
      active: activeMenuItem === 'BANQUETS',
    },
    {
      name: getTranslation(selectedLanguage, 'navigation.about'),
      url: '/pages/about',
      active: activeMenuItem === 'ABOUT US',
    },
    {
      name: getTranslation(selectedLanguage, 'navigation.contact'),
      url: '/pages/contact',
      active: activeMenuItem === 'CONTACT US',
    },
  ];

  const handleLanguageChange = langCode => {
    setSelectedLanguage(langCode);
    saveLanguage(langCode);
    setIsLocalizationOpen(false);
    window.location.reload();
  };

  const handleCurrencyChange = currCode => {
    setSelectedCurrency(currCode);
    setIsLocalizationOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setShowMobileDropdown(false);
  };

  const toggleMobileDropdown = () => {
    setShowMobileDropdown(!showMobileDropdown);
  };

  const handleMobileLangChange = langCode => {
    handleLanguageChange(langCode);
    setShowMobileDropdown(false);
  };

  const handleMobileCurrChange = currCode => {
    handleCurrencyChange(currCode);
    setShowMobileDropdown(false);
  };

  return (
    <header className='react-header'>
      <div className='header__top-bar'>
        <div className='container'>
          <div className='header__top-left'>
            <a
              className='header__phone'
              href={`tel:${settings.phone || '0438182912'}`}
            >
              {settings.order_call_text ||
                getTranslation(selectedLanguage, 'header.orderCall')}{' '}
              {settings.phone || '043 818 29 12'}
            </a>
          </div>
          <div className='header__top-right'>
            <div className='header__localization'>
              <div className='localization__dropdown'>
                <button
                  className='localization__trigger'
                  onClick={e => {
                    e.stopPropagation();
                    setIsLocalizationOpen(!isLocalizationOpen);
                  }}
                >
                  {selectedLanguage} / {selectedCurrency} ▼
                </button>
                <div
                  className={`localization__menu ${isLocalizationOpen ? 'is-open' : ''}`}
                >
                  <div className='localization__section'>
                    <h4>Language</h4>
                    {languages.map(lang => (
                      <button
                        key={lang.code}
                        onClick={e => {
                          e.stopPropagation();
                          handleLanguageChange(lang.code);
                        }}
                        className={
                          selectedLanguage === lang.code ? 'active' : ''
                        }
                      >
                        {lang.name}
                      </button>
                    ))}
                  </div>
                  <div className='localization__section'>
                    <h4>Currency</h4>
                    {currencies.map(curr => (
                      <button
                        key={curr.code}
                        onClick={e => {
                          e.stopPropagation();
                          handleCurrencyChange(curr.code);
                        }}
                        className={
                          selectedCurrency === curr.code ? 'active' : ''
                        }
                      >
                        {curr.code} ({curr.symbol})
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='header__main'>
        <div className='container'>
          <div className='header__content'>
            <div className='header__logo'>
              <a href='/' className='header__logo-link'>
                {settings.logo_type === 'text' ? (
                  <h1 className='logo__text'>
                    {settings.logo_text || 'DAMAS'}
                  </h1>
                ) : (
                  <h1>
                    <img
                      className='logo__image'
                      src={logoUrl}
                      alt='Restaurant Damas Logo'
                      style={{ height: `${settings.logo_height || 140}px` }}
                    />
                  </h1>
                )}
              </a>
            </div>

            <nav className={`header__nav ${isMenuOpen ? 'is-open' : ''}`}>
              <ul className='nav__list'>
                {menuItems.map((item, index) => (
                  <li
                    key={index}
                    className={`nav__item ${item.name === 'CATALOG' ? 'nav__item--dropdown' : ''}`}
                    onMouseEnter={() =>
                      item.name === 'CATALOG' && setShowCatalogMenu(true)
                    }
                    onMouseLeave={() =>
                      item.name === 'CATALOG' && setShowCatalogMenu(false)
                    }
                  >
                    <a
                      href={item.url}
                      className={`nav__link ${item.active ? 'active' : ''}`}
                    >
                      <b>
                        {item.name}
                        {item.name === 'CATALOG' && (
                          <span className='dropdown-arrow'>▼</span>
                        )}
                      </b>
                    </a>

                    {item.name === 'CATALOG' && showCatalogMenu && (
                      <div className='catalog-dropdown'>
                        <div className='catalog-dropdown__content'>
                          <ul className='catalog-dropdown__list'>
                            {collections.map(collection => (
                              <li
                                key={collection.id}
                                className='catalog-dropdown__item'
                              >
                                <a
                                  href={`/collections/${collection.handle}`}
                                  className='catalog-dropdown__link'
                                >
                                  {collection.image && (
                                    <img
                                      src={collection.image.src}
                                      alt={collection.title}
                                      className='catalog-dropdown__image'
                                    />
                                  )}
                                  <div className='catalog-dropdown__info'>
                                    <h3 className='catalog-dropdown__name'>
                                      {collection.title}
                                    </h3>
                                    {collection.subcategories && (
                                      <ul className='catalog-dropdown__subcategories'>
                                        {collection.subcategories.map(
                                          (subcategory, idx) => (
                                            <li
                                              key={idx}
                                              className='catalog-dropdown__subcategory'
                                            >
                                              <a
                                                href={`/collections/${subcategory.toLowerCase()}`}
                                              >
                                                {subcategory}
                                              </a>
                                            </li>
                                          )
                                        )}
                                      </ul>
                                    )}
                                  </div>
                                </a>
                              </li>
                            ))}
                          </ul>
                          <div className='catalog-dropdown__footer'>
                            <a
                              href='/collections'
                              className='catalog-dropdown__all'
                            >
                              MEHR LESEN
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </nav>

            <div className='header__cart'>
              <a href='/cart' className='cart__link'>
                <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
                  <path
                    d='M3 1H5L7 11H17L19 4H6'
                    stroke='currentColor'
                    strokeWidth='1.5'
                  />
                  <circle
                    cx='8'
                    cy='17'
                    r='1'
                    stroke='currentColor'
                    strokeWidth='1.5'
                  />
                  <circle
                    cx='16'
                    cy='17'
                    r='1'
                    stroke='currentColor'
                    strokeWidth='1.5'
                  />
                </svg>
                <span className='cart__text'>
                  {getTranslation(selectedLanguage, 'header.cart')}:
                </span>
                <span className='cart__count'>
                  {cartCount} {getTranslation(selectedLanguage, 'header.items')}
                </span>
              </a>
            </div>

            <button
              className={`header__mobile-toggle ${isMenuOpen ? 'active' : ''}`}
              onClick={toggleMobileMenu}
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
      </div>

      {isMenuOpen && (
        <div className='header__mobile-menu'>
          <button className='mobile-menu__close' onClick={toggleMobileMenu}>
            ✕
          </button>

          <div className='mobile-menu__content'>
            <div className='mobile-menu__top'>
              <div className='mobile-menu__phone'>
                <a href={`tel:${settings.phone || '0438182912'}`}>
                  {settings.order_call_text ||
                    getTranslation(selectedLanguage, 'header.orderCall')}{' '}
                  {settings.phone || '043 818 29 12'}
                </a>
              </div>
              <div className='mobile-menu__localization'>
                <div className='mobile-localization__dropdown'>
                  <button
                    className='mobile-localization__trigger'
                    onClick={toggleMobileDropdown}
                  >
                    {selectedLanguage} / {selectedCurrency} ▼
                  </button>
                  {showMobileDropdown && (
                    <div className='mobile-localization__menu'>
                      <div className='mobile-localization__section'>
                        <h4>
                          {getTranslation(selectedLanguage, 'header.language')}
                        </h4>
                        {languages.map(lang => (
                          <button
                            key={lang.code}
                            onClick={() => handleMobileLangChange(lang.code)}
                            className={
                              selectedLanguage === lang.code ? 'active' : ''
                            }
                          >
                            {lang.name}
                          </button>
                        ))}
                      </div>
                      <div className='mobile-localization__section'>
                        <h4>
                          {getTranslation(selectedLanguage, 'header.currency')}
                        </h4>
                        {currencies.map(curr => (
                          <button
                            key={curr.code}
                            onClick={() => handleMobileCurrChange(curr.code)}
                            className={
                              selectedCurrency === curr.code ? 'active' : ''
                            }
                          >
                            {curr.code} ({curr.symbol})
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <nav className='mobile-menu__nav'>
              <ul className='mobile-nav__list'>
                {menuItems.map((item, index) => (
                  <li key={index} className='mobile-nav__item'>
                    <a
                      href={item.url}
                      className={`mobile-nav__link ${item.active ? 'active' : ''}`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}
